<template>
    <div
      v-if="props.itemData && showStatus"
      ref="entryEffectRef"
      :id="props.itemData.config.id"
      :data-target-level-keys="disabled ? undefined : itemIdxStr"
      :data-target-level-disabled-keys="disabled ? itemIdxStr : undefined"
      :data-can-move-up="(idx > 0) + ''"
      :data-can-move-down="(idx < listLength - 1) + ''"
      :data-component-name="componentName"
      :class="['element-item-all-wrap', filterConfig?.classStyle || props.itemData.config.classStyle]"
      :style="styleObject"
      @mouseenter="hoverEffectEvents.mouseenter"
      @mouseleave="hoverEffectEvents.mouseleave"
    >
      <div
        id="element-item-wrap"
        :class="{
          'element-item-wrap': true,
          current: !disabled && currentElementIndexStr == itemIdxStr,
          'element-item-wrap-edit': !disabled,
          'element-item-wrap-disabled': disabled,
        }"
        :style="elementStyle"
      >
 
        <ClientOnly v-if="shouldRenderClientOnly">
          <component
            :disabled="disabled"
            :is="componentName"
            :allArcListMap="allArcListMap"
            :parentKeys="parentKeys"
            :idx="idx"
            :currentElementIndex="currentElementIndex"
            :config="filterConfig"
            :pages="pages"
            @resize="onResizeBox"
          />
        </ClientOnly>
        <component
          v-else
          :disabled="disabled"
          :is="componentName"
          :allArcListMap="allArcListMap"
          :parentKeys="parentKeys"
          :idx="idx"
          :currentElementIndex="currentElementIndex"
          :config="filterConfig"
          :pages="pages"
          @resize="onResizeBox"
        />
        
    
      </div>
    </div>
  </template>
  
  <script setup>
  import { computed, reactive } from 'vue';
  import { useElementIndex } from './uses/elementIndexUse'; // 引入composable
  import { useEntryEffect } from './uses/useEntryEffect.js';
  import { useHoverEffect } from './uses/useHoverEffect.js';
  import { isDisabledMallSsr } from '@/utils/globalData';
  import bus from '@/bus/index';
  
  // 定义 props
  const props = defineProps({
    allArcListMap: { type: Object, default: () => ({}) },
    currentElementIndex: { type: Array, default: () => [] },
    parentKeys: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: true },
    pages: { type: Array, default: () => [] },
    itemData: { type: Object },
    current: { type: Boolean, default: false },
    idx: { type: Number, default: -1 },
    listLength: { type: Number, default: 0 },
  });
  
  // 引用 composable
  const {
    platformType,
    renderData,
    rectBox,
    filterConfig,
    componentName,
    setStyle,
    showStatus,
    currentElementIndexStr,
    itemIdxStr,
    isPosition,
    selectElement,
    removeElement,
    copyElement,
    moveElementUp,
    moveElementDown,
    setRenderData,
  } = useElementIndex(props);

  const clientOnlyMallComponentSet = new Set([
    'ProductDetail1688Item',
    'MallGoodsCard',
  ]);

  const shouldRenderClientOnly = computed(() => {
    return isDisabledMallSsr() && clientOnlyMallComponentSet.has(componentName.value);
  });

  const { targetRef: entryEffectRef, entryEffectStyle } = useEntryEffect(
    () => filterConfig.value || props.itemData?.config || {},
  );
  const { hoverEffectEvents, wrapperHoverStyle, contentHoverStyle } = useHoverEffect(
    () => filterConfig.value || props.itemData?.config || {},
  );
  
  // 计算样式
  const styleObject = computed(() => {
    const config = filterConfig.value || props.itemData.config || {};
    const overflowHiddenStyle = config.overflowHidden
      ? { overflow: 'hidden', overflowX: 'hidden', overflowY: 'hidden' }
      : {};
    return {
      position: isPosition.value ? config.position : undefined,
      left: isPosition.value && config.positionVal?.left !== 'auto' ? `${config.positionVal.left}px` : undefined,
      top: isPosition.value && config.positionVal?.top !== 'auto' ? `${config.positionVal.top}px` : undefined,
      right: isPosition.value && config.positionVal?.right !== 'auto' ? `${config.positionVal.right}px` : undefined,
      bottom: isPosition.value && config.positionVal?.bottom !== 'auto' ? `${config.positionVal.bottom}px` : undefined,
      ...setStyle.value,
      ...entryEffectStyle.value,
      ...wrapperHoverStyle.value,
      border:null,
      borderLeft:null,
      borderRight:null,
      borderBottom:null,
      borderTop:null,
      "border-left":null,
      "border-right":null,
      "border-bottom":null,
      "border-top":null,
      ...overflowHiddenStyle,

    };
  });
  
  const elementStyle = computed(() => {
    const config = filterConfig.value || props.itemData.config || {};
    const overflowHiddenStyle = config.overflowHidden
      ? { overflow: 'hidden', overflowX: 'hidden', overflowY: 'hidden' }
      : {};
    return {
      border: config.shadow ? '1px solid #f3f3f3' : undefined,
      backgroundColor: config.backgroundColor,
      backgroundImage: config.backgroundImage ? `url(${config.backgroundImage})` : undefined,
      paddingTop: `${config.padding?.top || 0}px`,
      paddingBottom: `${config.padding?.bottom || 0}px`,
      paddingLeft: `${config.padding?.left || 0}px`,
      paddingRight: `${config.padding?.right || 0}px`,
      marginTop: `${config.margin?.top || 0}px`,
      marginBottom: `${config.margin?.bottom || 0}px`,
      marginLeft: `${config.margin?.left || 0}px`,
      marginRight: `${config.margin?.right || 0}px`,
      borderTopLeftRadius: config.radius ? `${config.radius.leftTop}px` : '0',
      borderTopRightRadius: config.radius ? `${config.radius.rightTop}px` : '0',
      borderBottomLeftRadius: config.radius ? `${config.radius.leftBottom}px` : '0',
      borderBottomRightRadius: config.radius ? `${config.radius.rightBottom}px` : '0',
      width: typeof config.width === 'number' ? `${config.width}px` : 'auto',
      height: typeof config.height === 'number' ? `${config.height}px` : 'auto',
      ...setStyle.value,
      "box-shadow":null,
      "boxShadow":null,
      ...contentHoverStyle.value,
      ...overflowHiddenStyle,
    };
  });
  
  // 事件监听
  const onResizeBox = (e) => {
    console.log('resize', e);
  };
  
  const onBgImageLoad = () => {
    console.log('bg image loaded');
  };
  </script>
  
  <style scoped>
  /* 样式与原本相同 */
  .element-item-wrap {
    position: relative;
    /* overflow: hidden; */
    background-size: 100% auto;
    background-position: center top;
  } 
  .element-item-wrap-edit:hover::before{
    display: block;
  }
 
  .element-item-wrap-edit::before {
    content: " ";
    position: absolute;
    left: 0;
    display: none;
    top: 0px;
    right: 0;
    bottom: 0;
    z-index: 1112;
    pointer-events: none;
    user-select: none;
    border: 1px dashed rgba(0, 0, 0, 0.3);
  }
  .element-item-wrap-edit.current::before{
    display: block;
    border:1px solid #4169e1 !important;
 
  }
  </style>
  
